/**
 * Graded problem block renderer.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

import { useAsterController } from "context";
import { getModuleIdFromCourseId, getQuestionMapFromIndex } from "utils/grade.util";
import ChoiceResponseRender from "./ChoiceResponseRender";
import EquationRender from "./EquationRender";
import MultipleChoiceResponseRender from "./MultipleChoiceResponseRender";
import NumericalResponseRender from "./NumericalResponseRender";
import OptionResponseRender from "./OptionResponseRender";
import PRender from "./PRender";
import SolutionRender from "./SolutionRender";
import StringResponseRender from "./StringResponseRender";
import { VButton } from "form";

/**
 * Parses problem OLX and collects learner answers for submit.
 *
 * @param {{ html: string, id: string, onSubmit?: Function }} props
 */
export default function Problem(props) {

  const { rawText, grade, courseId, blockId, onSubmitProblem } = props; // Problem block and it's grade

  const { t } = useTranslation('common');

  const [controller] = useAsterController();
  const { user } = controller;

  const [tags, setTags] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [isShowHint, setShowHint] = useState(false);
  const [isContainHint, setContainHint] = useState(false);
  const [isShowMark, setShowMark] = useState(false);

  useEffect(() => {
    const parser = new DOMParser();
    const xml = parser.parseFromString(rawText, 'text/xml');
    const questionList = ['optionresponse', 'multiplechoiceresponse', 'choiceresponse', 'numericalresponse', 'stringresponse'];
    const items = xml.querySelector('problem').children;

    // Map tag to its corresponding grade and set state
    let mappedItems = [];
    let qIndex = 0;
    for (let i = 0; i < items.length; i++) {
      const tagName = items[i].tagName ? items[i].tagName.toLowerCase() : null;
      if (!tagName) continue;

      // if there is solution tag, then, enable show hint button
      if (tagName === 'solution') setContainHint(true);

      // Map grade to its corresponding question tag ignoring other tags
      if (questionList.indexOf(tagName) > -1) {
        if (!grade || !grade.state || !grade.state.question_map) {
          // If there is no grade for this problem block, then create new one
          mappedItems.push({
            'tag': items[i],
            'grade': {
              'id': `${blockId}_${qIndex + 2}_1`,
              'correctness': null,
              'student_answer': null
            }
          });
        } else {
          mappedItems.push({ 'tag': items[i], 'grade': getQuestionMapFromIndex(grade.state?.question_map, qIndex) });
        }
        qIndex++;
      } else {
        mappedItems.push({ 'tag': items[i], 'grade': null });
      }
    }
    setTags(mappedItems);

    // Set answer state
    if (grade) {
      setAnswers(grade.state.question_map);
    } else { setAnswers([]); }

  }, [rawText, grade, blockId]);

  /**
   * Upserts a question answer in local state.
   *
   * @param {Object} ans Answer payload.
   * @returns {void}
   */
  const onSetAnswer = (ans) => {
    let tmp = [...answers];
    const itemIndex = tmp.findIndex(t => t.id === ans.id);

    itemIndex > -1 ? tmp[itemIndex] = ans : tmp.push(ans);
    setAnswers(tmp);
  };

  /**
   * Builds and posts the problem answer payload.
   *
   * @param {React.FormEvent} e Submit event.
   * @returns {void}
   */
  const onSubmit = (e) => {
    e.preventDefault();
    setShowMark(true);

    const answersToSend = answers.filter(ans => Object.keys(ans).length === 3);

    if (!answersToSend.length) return;

    const payload = {
      'student_id': user.id,
      'course_id': courseId,
      'module_id': grade ? grade.module_id : getModuleIdFromCourseId(courseId, blockId),
      'raw_possible': tags.filter(t => t.grade).length,
      'choices': answersToSend
    };
    onSubmitProblem(payload);
  };

  /**
   * Reveals problem hints.
   *
   * @returns {void}
   */
  const onShowHint = () => {
    setShowHint(!isShowHint);
  };

  return <>
    <form onSubmit={onSubmit} className="mt-10">
      {
        tags.map((t, key) => {
          const tag = t.tag ? t.tag.tagName : null;
          const tagName = tag ? tag.toLowerCase() : '';
          if (tagName === 'p1' || tagName === 'label') {
            return <PRender tag={t.tag} key={key} pkey={key} />;
          } else if (tagName === 'optionresponse') {
            return <OptionResponseRender // Select box
              key={key} pkey={key}
              tag={t.tag} grade={t.grade}
              onSetAnswer={onSetAnswer}
              isShowMark={isShowMark}
              setShowMark={setShowMark}
            />;

          } else if (tagName === 'multiplechoiceresponse') {
            return <MultipleChoiceResponseRender  // Radio
              key={key} pkey={key} blockId={blockId}
              tag={t.tag} grade={t.grade}
              onSetAnswer={onSetAnswer}
              isShowMark={isShowMark}
              setShowMark={setShowMark} />;
          } else if (tagName === 'choiceresponse') {
            return <ChoiceResponseRender  // Checkbox
              key={key} pkey={key} blockId={blockId}
              tag={t.tag} grade={t.grade}
              onSetAnswer={onSetAnswer}
              isShowMark={isShowMark}
              setShowMark={setShowMark}
            />;
          } else if (tagName === 'numericalresponse') {
            return <NumericalResponseRender
              key={key} pkey={key}
              tag={t.tag} grade={t.grade}
              onSetAnswer={onSetAnswer}
              isShowMark={isShowMark}
              setShowMark={setShowMark}
            />;
          } else if (tagName === 'stringresponse') {
            return <StringResponseRender
              key={key} pkey={key}
              tag={t.tag} grade={t.grade}
              onSetAnswer={onSetAnswer}
              isShowMark={isShowMark}
              setShowMark={setShowMark}
            />;
          } else if (tagName === 'center' || tagName === 'p') {
            return <EquationRender key={key} tag={t.tag} />;
          } else if (tagName === 'solution') {
            return <SolutionRender tag={t.tag} key={key} isShowHint={isShowHint} />;
          }
          return <>Undefined</>;
        })
      }
      <hr className="mt-5 mb-4 color-main2" />
      <div className="w-full flex mb-3">
        <div className="ml-auto">
          <VButton type="submit" variant="contained" className="btn v-bg-body bg-main1 text-muted border border-1 p-btn fsp-16 fw-500 lh-22 hover-border transition-all ml-2">
            {t('Submit')}
          </VButton>
          
          {
            isContainHint &&
            <VButton onClick={onShowHint} type="button" variant="contained" className="btn v-bg-body bg-main1 text-muted border border-1 p-btn fsp-16 fw-500 lh-22 hover-border transition-all ml-2">
              {isShowHint ? t('Hide hint') : t('Show hint')}
            </VButton>
          }

        </div>
      </div>
    </form>
  </>;
}